/**
 * Where outbound links should point during the btvision.app migration.
 *
 * Invite and share links used to be built from `window.location.origin`. That
 * meant an organizer whose PWA is installed on the old domain minted old-domain
 * invites, so every player they recruited installed onto the origin we are
 * retiring — the old domain reproducing itself faster than a migration banner
 * can drain it. Links leaving the app are pinned to the canonical domain
 * instead, whichever production origin generated them.
 *
 * Only production hosts are pinned. localhost, staging and Netlify deploy
 * previews keep returning their own origin, so a link copied out of those still
 * points back at the environment being tested.
 *
 * Deliberately NOT used for auth redirects. `emailRedirectTo` must stay on the
 * current origin: sending an old-domain user to the new one mid-login drops
 * them into a browser tab with empty localStorage, outside their own install.
 * See §3 of plans/BTVISION_DOMAIN_MIGRATION_PLAN.md.
 */

export const CANONICAL_ORIGIN = 'https://btvision.app';

/** The origin BT Vision is migrating away from. */
export const LEGACY_HOST = 'btvision-app.retrospectivabeach.online';

const PRODUCTION_HOSTS = ['btvision.app', 'www.btvision.app', LEGACY_HOST];

/** Base URL for links meant to be opened by someone else, later. */
export function canonicalOrigin(): string {
  return PRODUCTION_HOSTS.includes(window.location.hostname)
    ? CANONICAL_ORIGIN
    : window.location.origin;
}

/**
 * True when this tab is served from the domain being retired.
 *
 * `?legacy=1` forces it on anywhere. Without an escape hatch the entire Phase 3
 * UI is unreachable outside production — staging is not the old origin, so the
 * migration banner and its reinstall walkthrough could only ever be reviewed by
 * shipping them. The override shows a banner and nothing else; there is no
 * state it can corrupt.
 */
export function isLegacyOrigin(): boolean {
  if (window.location.hostname === LEGACY_HOST) return true;
  try {
    return new URLSearchParams(window.location.search).get('legacy') === '1';
  } catch {
    return false;
  }
}
